import { useState } from "react";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  FormGroup,
  FormLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { Company } from "models/Company";
import EditOutlined from "@mui/icons-material/EditOutlined";
import { useGetOfficesQuery } from "app/api/companies";
import LoadingDisplay from "components/LoadingDisplay";
import EmptyMessage from "components/EmptyMessage";
import OfficeForm from "../components/OfficeForm";
import Add from "@mui/icons-material/Add";
import DataLabel from "components/DataLabel";

type OfficesSectionProps = { company: Company };

const OfficesSection: React.FC<OfficesSectionProps> = ({ company }) => {
  const [showAddOfficeDialog, setShowAddOfficeDialog] = useState(false);
  const [editOfficeId, setEditOfficeId] = useState<string | null>(null);

  const { data: offices, isLoading: isLoadingOffices } = useGetOfficesQuery({
    companyId: company.id,
  });

  return (
    <>
      <DialogContent sx={{ p: 1 }}>
        {isLoadingOffices ? (
          <LoadingDisplay message="Hämtar kontor..." />
        ) : (
          <List disablePadding>
            {!offices?.length && (
              <ListItem divider>
                <ListItemText primary={<EmptyMessage>Inga kontor hittades</EmptyMessage>} />
              </ListItem>
            )}
            {offices?.map((office) =>
              office.id === editOfficeId ? (
                <ListItem divider sx={{ justifyContent: "center" }} key={office.id}>
                  <OfficeForm
                    company={company}
                    officeToEdit={office}
                    onClose={() => setEditOfficeId(null)}
                  />
                </ListItem>
              ) : (
                <ListItem
                  key={office.id}
                  divider
                  secondaryAction={
                    <IconButton
                      onClick={() => {
                        setShowAddOfficeDialog(false);
                        setEditOfficeId(office.id);
                      }}
                    >
                      <EditOutlined />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={office.address}
                    secondary={
                      <>
                        <Typography>
                          {office.postalCode} {office.city}
                        </Typography>
                        <DataLabel label="Timpris">
                          {office.hourlyRate ? `${office.hourlyRate} kr/h` : "-"}
                        </DataLabel>
                      </>
                    }
                    slotProps={{ primary: { fontWeight: "bold" } }}
                  />
                </ListItem>
              )
            )}
          </List>
        )}
      </DialogContent>

      {!showAddOfficeDialog && (
        <DialogActions>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setEditOfficeId(null);
              setShowAddOfficeDialog(true);
            }}
            sx={{ ml: "auto" }}
            disabled={isLoadingOffices}
          >
            Nytt kontor
          </Button>
        </DialogActions>
      )}

      {showAddOfficeDialog && (
        <Box display="flex" justifyContent="center" p={1}>
          <OfficeForm company={company} onClose={() => setShowAddOfficeDialog(false)} />
        </Box>
      )}
    </>
  );
};

export default OfficesSection;
